import React, { useMemo, useState } from 'react';
import {
  SIZES,
  serviceFlags,
  categoryFlags,
  catalogTotals,
  serviceOpFlags,
  categoryOpFlags,
  operationalTotals,
} from './catalogValidation';
import './serviceCatalog.css';

/**
 * Single "Need Data" pill used consistently across Pricing + Operations (summary
 * bar, category rows, edit page). Only renders when there's something to act on
 * (count > 0); a clean state shows nothing — no reassurance pill.
 */
export function needDataPill(count) {
  if (!count) return null;
  return <span className="jv-badge jv-badge-info jv-badge-pill"><i className="fa-sharp fa-solid fa-circle-info" /> {count} need data</span>;
}

/**
 * ServiceCatalogGrid — the redesigned Services landing.
 *
 * Renders the full catalog as a sticky pricing matrix (Pricing mode) or an
 * expected-qty + duration matrix (Operations mode), with error/warning flags
 * derived at render time. Presentational + local UI state only; the container
 * owns data loading and navigation.
 *
 * Props:
 *   catalog        Array  normalized categories (see catalogValidation.normalizeCatalog)
 *   canEdit        bool   show Add Service / allow edit affordances
 *   onOpenService  (id) => void   row click → service edit
 *   onAddService   () => void     Add Service button
 */
export default function ServiceCatalogGrid({ catalog = [], canEdit = false, onOpenService, onAddService }) {
  const [opMode, setOpMode] = useState('pricing'); // 'pricing' | 'operations'
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({}); // { [catId]: true }

  const totals = useMemo(() => catalogTotals(catalog), [catalog]);
  const opsTotals = useMemo(() => operationalTotals(catalog), [catalog]);

  // Apply search filter (by service name) to a working copy of the catalog.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;
    return catalog
      .map((cat) => ({ ...cat, services: cat.services.filter((s) => s.name.toLowerCase().includes(term)) }))
      .filter((cat) => cat.services.length > 0);
  }, [catalog, search]);

  const toggleCat = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  return (
    <div className="jv-card">
      {/* Filter / summary bar */}
      <div className="sc-summary-bar">
        <div className="jv-btn-group">
          <button
            type="button"
            className={`jv-btn jv-btn-sm ${opMode === 'pricing' ? 'jv-btn-primary' : 'jv-btn-ghost'}`}
            onClick={() => setOpMode('pricing')}
          >
            <i className="fa-sharp fa-regular fa-tag" /> Pricing
          </button>
          <button
            type="button"
            className={`jv-btn jv-btn-sm ${opMode === 'operations' ? 'jv-btn-primary' : 'jv-btn-ghost'}`}
            onClick={() => setOpMode('operations')}
          >
            <i className="fa-sharp fa-regular fa-gauge-high" /> Operations
          </button>
        </div>

        <div className="sc-divider" />

        {opMode === 'pricing' ? (
          <React.Fragment>
            {needDataPill(totals.errors + totals.warnings)}
            <span className="jv-badge jv-badge-neutral">
              {totals.services} services · {totals.categories} categories
            </span>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {needDataPill(opsTotals.needsData)}
            <span className="jv-badge jv-badge-neutral">
              {opsTotals.services} services · {opsTotals.categories} categories
            </span>
          </React.Fragment>
        )}

        <div className="sc-spacer" />

        <div className="sc-search">
          <i className="fa-sharp fa-regular fa-magnifying-glass" />
          <input
            className="jv-input jv-input-sm"
            type="text"
            placeholder="Search services…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={`jv-btn jv-btn-sm ${issuesOnly ? 'jv-btn-primary' : 'jv-btn-ghost'}`}
          onClick={() => setIssuesOnly((v) => !v)}
        >
          <i className="fa-sharp fa-solid fa-filter" /> Need Data
        </button>

        {canEdit && (
          <button type="button" className="jv-btn jv-btn-sm jv-btn-primary" onClick={onAddService}>
            <i className="fa-sharp fa-solid fa-plus" /> Add Service
          </button>
        )}
      </div>

      {opMode === 'pricing' ? (
        <PricingGrid
          catalog={filtered}
          issuesOnly={issuesOnly}
          collapsed={collapsed}
          toggleCat={toggleCat}
          onOpenService={onOpenService}
        />
      ) : (
        <OperationsGrid
          catalog={filtered}
          issuesOnly={issuesOnly}
          collapsed={collapsed}
          toggleCat={toggleCat}
          onOpenService={onOpenService}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Pricing grid                                                              */
/* ------------------------------------------------------------------------- */

function PricingGrid({ catalog, issuesOnly, collapsed, toggleCat, onOpenService }) {
  const cats = buildVisibleCats(catalog, issuesOnly, collapsed, 'pricing');

  return (
    <React.Fragment>
      {/* Legend */}
      <div className="sc-legend">
        <span className="sc-legend__label">Legend</span>
        <span><span className="sc-price is-error" style={{ borderLeft: 0 }}>——</span> Missing</span>
        <span><span className="sc-price is-error" style={{ borderLeft: 0 }}>$0</span> Zero</span>
        <span><span className="jv-badge jv-badge-warning">R&lt;W</span> Inverted</span>
        <span><span className="jv-badge jv-badge-neutral">= FLAT</span> Flat (ok)</span>
      </div>

      <div className="sc-matrix-wrap">
        <table className="sc-matrix jv-table jv-table-sm">
          <thead>
            <tr>
              <th rowSpan={2} className="sc-col-service sc-th-r0">Service</th>
              <th colSpan={8} className="sc-th-r0 sc-th-group">Pricing ($) — retail / wholesale</th>
              <th rowSpan={2} className="sc-th-r0 sc-divider-left" style={{ textAlign: 'center', verticalAlign: 'bottom' }}>Dur</th>
            </tr>
            <tr>
              {SIZES.map((s, i) => (
                <React.Fragment key={s}>
                  <th className={`sc-th-r1 ${i === 0 ? 'sc-divider-left' : ''}`}>{s}·R</th>
                  <th className="sc-th-r1">{s}·W</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {cats.length === 0 && (
              <tr><td colSpan={10} className="sc-grid-empty">No services match.</td></tr>
            )}
            {cats.map((cat) => (
              <React.Fragment key={cat.id}>
                <CategoryRow cat={cat} colSpan={10} onToggle={() => toggleCat(cat.id)} />
                {cat.rows.map((svc) => (
                  <PricingRow key={svc.id} svc={svc} onOpen={() => onOpenService(svc.id)} />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

function PricingRow({ svc, onOpen }) {
  const f = serviceFlags(svc);
  return (
    <tr className="sc-svc-row">
      <td className="sc-col-service">
        <div className="sc-svc-cell" onClick={onOpen}>
          <span className="sc-svc-link">{svc.name}</span>
          {svc.isCustom && <span className="jv-badge jv-badge-neutral"><i className="fa-sharp fa-regular fa-lock" /> Custom</span>}
          {svc.cov && <span className="jv-badge jv-badge-neutral">{svc.cov}</span>}
          {svc.vex > 0 && (
            <span className="jv-badge jv-badge-info"><i className="fa-sharp fa-regular fa-car" /> {svc.vex} veh</span>
          )}
          {f.flat && <span className="jv-badge jv-badge-neutral">= FLAT</span>}
        </div>
      </td>
      {f.cells.map((cell, i) => {
        const isSR = cell.size === 'S' && cell.tier === 'R';
        const cls = [
          'sc-price',
          cell.tier === 'R' ? (isSR ? 'sc-price--sr' : 'sc-price--r') : '',
          cell.state === 'error' ? 'is-error' : cell.state === 'warn' ? 'is-warn' : '',
        ].filter(Boolean).join(' ');
        return <td key={i} className={cls} title={cell.title || undefined}>{cell.text}</td>;
      })}
      <td className="sc-dur">{f.noDur ? '——' : svc.dur}</td>
    </tr>
  );
}

/* ------------------------------------------------------------------------- */
/* Operations grid (v1: read/plain — no OBD accept flow)                     */
/* ------------------------------------------------------------------------- */

function OperationsGrid({ catalog, issuesOnly, collapsed, toggleCat, onOpenService }) {
  const cats = buildVisibleCats(catalog, issuesOnly, collapsed, 'operations');

  return (
    <React.Fragment>
      <div className="sc-matrix-wrap">
        <table className="sc-matrix jv-table jv-table-sm">
          <thead>
            <tr>
              <th rowSpan={2} className="sc-col-service sc-th-r0">Service</th>
              <th colSpan={4} className="sc-th-r0 sc-th-group sc-th-group--muted">Expected qty (×)</th>
              <th colSpan={4} className="sc-th-r0 sc-th-group sc-th-group--muted">Duration (days)</th>
            </tr>
            <tr>
              {SIZES.map((s, i) => (
                <th key={`q${s}`} className={`sc-th-r1 ${i === 0 ? 'sc-divider-left' : ''}`}>{s}</th>
              ))}
              {SIZES.map((s, i) => (
                <th key={`d${s}`} className={`sc-th-r1 ${i === 0 ? 'sc-divider-left' : ''}`} style={{ textAlign: 'center' }}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cats.length === 0 && (
              <tr><td colSpan={9} className="sc-grid-empty">No services match.</td></tr>
            )}
            {cats.map((cat) => (
              <React.Fragment key={cat.id}>
                <CategoryRow cat={cat} colSpan={9} onToggle={() => toggleCat(cat.id)} />
                {cat.rows.map((svc) => (
                  <OperationsRow key={svc.id} svc={svc} onOpen={() => onOpenService(svc.id)} />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}

function OperationsRow({ svc, onOpen }) {
  return (
    <tr className="sc-svc-row">
      <td className="sc-col-service">
        <div className="sc-svc-cell" onClick={onOpen}>
          <span className="sc-svc-link">{svc.name}</span>
          {svc.cov && <span className="jv-badge jv-badge-neutral">{svc.cov}</span>}
        </div>
      </td>
      {SIZES.map((s, i) => {
        const v = svc.q[s];
        const missing = !svc.isCustom && (v === null || v === undefined || v === 0);
        return (
          <td key={`q${s}`} className={`sc-op sc-op--qty ${i === 0 ? 'sc-op--first' : ''} ${missing ? 'sc-op__missing' : 'sc-op__known'}`}>
            {svc.isCustom ? '—' : missing ? '——' : v}
          </td>
        );
      })}
      {SIZES.map((s, i) => {
        const v = svc.d[s];
        const missing = !svc.isCustom && (v === null || v === undefined || v === 0);
        return (
          <td key={`d${s}`} className={`sc-op ${i === 0 ? 'sc-op--first' : ''} ${missing ? 'sc-op__missing' : 'sc-op__known'}`} style={{ textAlign: 'center' }}>
            {svc.isCustom ? '—' : missing ? '——' : v}
          </td>
        );
      })}
    </tr>
  );
}

/* ------------------------------------------------------------------------- */
/* Shared                                                                    */
/* ------------------------------------------------------------------------- */

function CategoryRow({ cat, colSpan, onToggle }) {
  return (
    <tr className="sc-cat-row" onClick={onToggle}>
      <td colSpan={colSpan}>
        <div className="sc-cat-head">
          <i className={`fa-sharp fa-solid ${cat.collapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`} />
          <span className="sc-cat-name">{cat.name}</span>
          <span className="sc-cat-count">{cat.services.length} services</span>
          <span className="sc-spacer" />
          {needDataPill(cat.mode === 'operations' ? cat.needs : (cat.err + cat.warn))}
        </div>
      </td>
    </tr>
  );
}

/**
 * Compute visible categories + rows honoring collapse + issues-only.
 * issuesOnly force-expands and filters to flagged services / non-clean categories.
 */
function buildVisibleCats(catalog, issuesOnly, collapsed, mode) {
  return catalog
    .map((cat) => {
      const isCollapsed = issuesOnly ? false : !!collapsed[cat.id];
      let rows = cat.services;
      if (mode === 'operations') {
        const { needs, complete } = categoryOpFlags(cat.services);
        if (issuesOnly) rows = rows.filter((s) => serviceOpFlags(s).needsData);
        else if (isCollapsed) rows = [];
        return { ...cat, rows, mode, needs, complete, collapsed: isCollapsed };
      }
      const { err, warn, clean } = categoryFlags(cat.services);
      if (issuesOnly) rows = rows.filter((s) => serviceFlags(s).hasIssue);
      else if (isCollapsed) rows = [];
      return { ...cat, rows, mode, err, warn, clean, collapsed: isCollapsed };
    })
    .filter((cat) => {
      if (!issuesOnly) return true;
      return mode === 'operations' ? !cat.complete : !cat.clean;
    });
}
